import { type ChatTurn, ChatThread, type FieldDef, type ObjectMetadata } from '@olytic/foundation';

import { OBJECT_DEFS } from '../../objects/objectDefs';
import { asDateTime, asLabel, createdByField, field, lastModifiedByField, lookup, type Row, text } from './kit';

const def = OBJECT_DEFS['agent-conversations'];

/** The stored transcript ({ role, content, timestamp }[]) as the library's chat turns. */
export function transcriptTurns(value: unknown): ChatTurn[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((message, index) => {
    if (!message || typeof message !== 'object') return [];
    const { role, content, timestamp } = message as Record<string, unknown>;
    if ((role !== 'user' && role !== 'assistant') || typeof content !== 'string' || content === '') return [];
    return [{ id: String(index), role, content, ...(typeof timestamp === 'string' ? { timestamp } : {}) }];
  });
}

/** The whole chat, read only, oldest first. */
const transcript: FieldDef<Row> = {
  key: 'transcript',
  label: 'Transcript',
  wide: true,
  render: (row) => {
    const turns = transcriptTurns(row.transcript);
    return turns.length === 0 ? '—' : <ChatThread turns={turns} autoScroll={false} aria-label="Transcript" />;
  },
};

/** A chat with one of the website's agents (Ollie on olytic.com). Rationale: agent-conversations.md. */
export const agentConversations: ObjectMetadata<Row> = {
  key: def.key,
  singular: 'Agent Conversation',
  plural: def.label,
  icon: def.icon,
  listColumns: def.columns,
  details: [
    field('visitor_email', 'Visitor Email'),
    field('lead_name', 'Lead', { link: lookup('leads', 'lead_id') }),
    field('outcome', 'Outcome', { format: asLabel }),
    field('page_origin', 'Page'),
    field('message_count', 'Messages'),
    field('summary', 'Summary', { wide: true }),
    transcript,
    field('agent_type', 'Agent', { format: asLabel }),
    field('session_id', 'Session ID'),
    field('created_at', 'Started', { format: asDateTime }),
    field('updated_at', 'Last Message', { format: asDateTime }),
    field('completed_at', 'Lead Captured', { format: asDateTime }),
    createdByField(),
    lastModifiedByField(),
  ],
  sections: [
    { key: 'identity', fields: ['visitor_email', 'lead_name', 'outcome', 'page_origin', 'message_count', 'summary'] },
    { key: 'transcript', label: 'Transcript', fields: ['transcript'] },
    { key: 'system', label: 'System', collapsed: true, fields: ['agent_type', 'session_id', 'created_at', 'created_by_name', 'updated_at', 'last_modified_by_name', 'completed_at'] },
  ],
  related: [],
  titleOf: (row) => text(row.name),
  searchKeys: ['name', 'visitor_email', 'opening_message', 'page_origin'],
};
